// The Symbol Vault, and what sits behind one symbol.
//
// A count on its own ("darkness ×7") is a claim without a receipt. This joins the two halves the app
// already had but never connected: the occurrences, which live in the entries in the browser, and
// Oreia's reading of the symbol, which lives encrypted in the patterns endpoint. Opening a symbol
// should answer three questions in order — what it tends to mean, what it seems to mean for *you*,
// and every night it actually turned up, each one openable.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEntries } from '../entries/EntriesProvider';
import { usePortrait } from '../portrait/usePortrait';
import { useSession } from '../auth/SessionProvider';
import { EmptyState, Spinner } from '../components/ui';
import { IconArrow, IconLock } from '../components/Icons';
import { SymbolOrb } from '../symbols/SymbolOrb';
import { PatternArc } from '../symbols/PatternArc';
import { countedSymbols, publishedEntries, symbolKey } from './home-support';
import { symbolMeaning } from '../symbols/meanings';
import { CitedObservation } from '../components/CitedObservation';
import { EntryRow } from '../entries/EntryRow';
import { shortDate } from '../format/dates';
import { arcSummary, fetchPatternTimeline, type PatternTimeline } from '../api/pattern-timeline';

/**
 * Entries carry a bare symbol ("water"); the extraction pipeline stores a namespaced canonical id
 * ("symbol:water"). Comparing them raw finds nothing, which would silently leave every symbol looking
 * as though Oreia had never formed a reading of it.
 */
function bare(value: string): string {
  return symbolKey(value);
}

function sameSymbol(a: string, b: string): boolean {
  return bare(a) === bare(b);
}

export function SymbolVaultScreen() {
  const { entries: allEntries, isLoading } = useEntries();
  const entries = useMemo(() => publishedEntries(allEntries), [allEntries]);
  const symbols = useMemo(() => countedSymbols(entries), [entries]);

  return (
    <>
      <div className="ohead">
        <div className="obrand">
          <span className="nm" style={{ fontSize: 22 }}>Symbol Vault</span>
        </div>
        <div className="olock">
          <IconLock /> Private
        </div>
      </div>

      <div className="ojhead">
        <h1>
          What keeps
          <br />
          <em className="oitalic">coming back.</em>
        </h1>
        <p className="ojsub">
          {symbols.length === 0
            ? 'No symbols yet — they appear as you write.'
            : `${symbols.length} ${symbols.length === 1 ? 'symbol' : 'symbols'} across ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`}
        </p>
      </div>

      {isLoading ? (
        <Spinner label="Decrypting your entries…" />
      ) : symbols.length === 0 ? (
        <EmptyState
          title="Nothing recurring yet."
          body="Symbols are the images your entries keep returning to. Write a few and they start to gather here."
        />
      ) : (
        <div className="ocard ocard-light">
          <div className="osyms vault">
            {symbols.map((symbol) => (
              <Link className="osym osym-link" key={symbol.name} to={`/symbols/${encodeURIComponent(symbol.name)}`}>
                <SymbolOrb name={symbol.name} />
                <div className="snm">{symbol.name}</div>
                <div className="smono">×{symbol.count}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div style={{ height: 60 }} />
    </>
  );
}

export function SymbolDetailScreen() {
  const { name = '' } = useParams();
  const symbol = decodeURIComponent(name);
  const navigate = useNavigate();
  const { entries, isLoading } = useEntries();
  const portrait = usePortrait();
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;

  // Oreia's own reading, if this symbol has recurred enough for the nightly pass to have written one.
  const pattern = useMemo(
    () => portrait.patterns.find((p) => sameSymbol(p.canonicalId, symbol)) ?? null,
    [portrait.patterns, symbol],
  );

  // And if not: how close it is. Reported by the server against the same window promotion uses, so
  // the number here is the number the bar is actually judged on.
  const progress = useMemo(
    () => portrait.emerging.find((e) => sameSymbol(e.canonicalId, symbol)) ?? null,
    [portrait.emerging, symbol],
  );

  // The route is keyed by the bare word, but the history lives under a namespaced id. Rather than
  // guess at the slug, take the id from whichever record already knows it — and if neither does,
  // extraction has never seen this symbol and there is genuinely no history to ask for.
  const canonicalId = pattern?.canonicalId ?? progress?.canonicalId ?? null;
  const [timeline, setTimeline] = useState<PatternTimeline | null>(null);

  useEffect(() => {
    if (!dek || !canonicalId) { setTimeline(null); return; }
    const controller = new AbortController();
    fetchPatternTimeline(dek, canonicalId, controller.signal)
      // The arc is an elaboration on the page, not the page. If it cannot be fetched the symbol
      // still has a meaning and a list of entries, and those are the parts that matter.
      .then((result) => { if (!controller.signal.aborted) setTimeline(result); })
      .catch(() => { if (!controller.signal.aborted) setTimeline(null); });
    return () => controller.abort();
  }, [dek, canonicalId]);

  // Where this symbol actually turned up. Two sources, because they know different things: the
  // Symbols field is what the user tagged by hand, and the extraction log is what Oreia recognised
  // in the writing itself — including in entries the user never tagged. Reading only the first is
  // what made this list look empty for themes and beliefs, which are never typed into that field.
  const occurrences = useMemo(() => {
    const tagged = publishedEntries(entries).filter((entry) => entry.symbols.some((s) => sameSymbol(s, symbol)));
    if (!timeline) return tagged;

    const seen = new Set(tagged.map((e) => e.id));
    const extracted = timeline.sightings
      .map((s) => s.entryId)
      .filter((id): id is string => !!id && !seen.has(id))
      .map((id) => entries.find((e) => e.id === id))
      .filter((e): e is NonNullable<typeof e> => !!e);

    return [...tagged, ...extracted].sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
    );
  }, [entries, symbol, timeline]);

  const general = symbolMeaning(symbol);
  const dreams = occurrences.filter((e) => e.kind === 'dream').length;

  return (
    <>
      <div className="ohead">
        <button className="ocal-nav" onClick={() => navigate(-1)} aria-label="Back">
          <IconArrow style={{ transform: 'rotate(180deg)' }} />
        </button>
        <div className="olock">
          <IconLock /> Private
        </div>
      </div>

      <div className="osym-hero">
        <SymbolOrb name={symbol} />
        <div>
          <h1 className="osym-title">{symbol}</h1>
          <p className="ojsub" style={{ marginTop: 4 }}>
            {occurrences.length} {occurrences.length === 1 ? 'appearance' : 'appearances'}
            {dreams > 0 ? ` · ${dreams} in ${dreams === 1 ? 'a dream' : 'dreams'}` : ''}
          </p>
        </div>
      </div>

      {isLoading ? <Spinner label="Decrypting your entries…" /> : null}

      {/* The common reading first, then yours. Stated in that order because the general meaning is
          background: what matters is how it differs from what this symbol does in your own writing. */}
      {general ? (
        <div className="ocard ocard-light">
          <span className="olabel">Often a symbol of</span>
          <p className="obody" style={{ margin: '10px 0 0' }}>{general}</p>
        </div>
      ) : null}

      {pattern && pattern.meaning.trim() ? (
        <div className="ocard ocard-deep" style={{ marginTop: 12 }}>
          <span className="olabel" style={{ color: 'var(--accent-1)' }}>For you</span>
          <CitedObservation
            as="p"
            className="obody"
            style={{ margin: '10px 0 0' }}
            text={pattern.meaning}
            citations={pattern.meaningCitations}
          />
          <div className="ometa" style={{ marginTop: 14 }}>
            <span className="omono">
              seen-{pattern.occurrences}×
              {pattern.firstSeen ? ` · since-${shortDate(new Date(pattern.firstSeen))}` : ''}
              {pattern.trajectory ? ` · ${pattern.trajectory}` : ''}
            </span>
          </div>
        </div>
      ) : occurrences.length > 0 ? (
        <div className="ocard ocard-light" style={{ marginTop: 12 }}>
          <span className="olabel">For you</span>
          <p className="obody" style={{ margin: '10px 0 0', color: 'var(--ink-soft)' }}>
            {/* Deliberately not invented on the spot: a reading is written by the nightly pass once
                there is enough to read. Saying so is better than filling the space with something
                that sounds like insight. */}
            Oreia hasn't formed a reading of this one yet. It writes one once a symbol has recurred
            enough to say something honest about it.
          </p>
          {progress ? <SymbolProgress have={progress.have} need={progress.need} /> : null}
        </div>
      ) : null}

      {/* The arc sits after the reading and before the entries, because it answers the question
          between them: the reading says what this means, the entries say where it happened, and
          this says how it moved. One month of data is a dot, not a history, so it waits for two. */}
      {timeline && timeline.months.length > 1 ? (
        <div className="ocard ocard-light" style={{ marginTop: 12 }}>
          <span className="olabel">Over time</span>
          <p className="obody" style={{ margin: '10px 0 4px', color: 'var(--ink-soft)' }}>
            {arcSummary(timeline)}
          </p>
          <PatternArc timeline={timeline} summary={arcSummary(timeline)} />
          {timeline.dreamCount > 0 && timeline.wakingCount > 0 ? (
            <div className="ometa">
              <span className="omono">
                {timeline.dreamCount} in {timeline.dreamCount === 1 ? 'a dream' : 'dreams'} ·{' '}
                {timeline.wakingCount} awake
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="olabel" style={{ display: 'block', margin: '26px 0 12px 2px' }}>
        Where it appeared
      </div>

      {occurrences.length === 0 ? (
        <EmptyState title="Not in any entry." body="This symbol isn't in your journal anymore." />
      ) : (
        occurrences.map((entry) => (
          <EntryRow
            key={entry.id}
            entry={entry}
            onOpen={() => navigate(`/write/${entry.id}`)}
            // Occurrences of one symbol can be months apart, so the row says which day it was
            // rather than what time of day — the feed's answer, where the date is already overhead.
            when={shortDate(entry.createdAt)}
          />
        ))
      )}

      <div style={{ height: 60 }} />
    </>
  );
}

/**
 * How close a symbol is to earning a reading.
 *
 * "Seen twice — once more" turns a dead end into a nearly. The count is the server's, measured the
 * same way promotion measures it, which matters more than it sounds: the number of entries this
 * symbol appears in is usually larger, and using that would promise a reading that never arrives.
 *
 * Hidden at zero. A symbol whose sightings have all aged out of the window is not on its way
 * anywhere, and "0 of 3" is a scolding rather than an encouragement.
 */
export function SymbolProgress({ have, need }: { have: number; need: number }) {
  if (have <= 0 || need <= 0 || have >= need) return null;
  const short = need - have;

  return (
    <div className="osym-progress">
      <div className="osym-pips" aria-hidden>
        {Array.from({ length: need }, (_, i) => (
          <span key={i} className={i < have ? 'is-on' : ''} />
        ))}
      </div>
      <span className="omono">
        seen {have === 1 ? 'once' : have === 2 ? 'twice' : `${have} times`} ·{' '}
        {short === 1 ? 'once more' : `${short} more`} and Oreia writes one
      </span>
    </div>
  );
}

